import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import socket, { postRequest, getRequest } from '../../config';
import ChatMessages from './ChatMessages';
import ChatInput from './ChatInput';
import AgentActions from './AgentActions';
import SuggestedPrompts from './SuggestedPrompts';
import ClassificationConfirm from './ClassificationConfirm';
import './clarence.css';

const ClarenceChat = ({ companyId, onClose, onExpand, isExpanded }) => {
    const user = useSelector(state => state['user']);
    const [conversationId, setConversationId] = useState(null);
    const [conversations, setConversations] = useState([]);
    const [messages, setMessages] = useState([]);
    const [agentActions, setAgentActions] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingHistory, setIsLoadingHistory] = useState(true);
    const [pendingClassification, setPendingClassification] = useState(null);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [showHistory, setShowHistory] = useState(true);
    const [isCreatingConversation, setIsCreatingConversation] = useState(false);
    const messagesEndRef = useRef(null);
    const chatInputRef = useRef(null);

    // Load conversation history on mount
    useEffect(() => {
        if (!companyId) return;
        loadConversations();
    }, [companyId]);

    // Socket listeners
    useEffect(() => {
        socket.emit('register', { userId: user?.['_id'] || user?.['id'] });

        socket.on('clarence:action', handleAgentAction);
        socket.on('clarence:response', handleAgentResponse);
        socket.on('clarence:classification', handleClassification);

        return () => {
            socket.off('clarence:action', handleAgentAction);
            socket.off('clarence:response', handleAgentResponse);
            socket.off('clarence:classification', handleClassification);
        };
    }, []);

    // Auto-scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, agentActions]);

    // Auto-focus input after response
    useEffect(() => {
        if (!isLoading) {
            chatInputRef.current?.focus();
        }
    }, [isLoading]);

    const loadConversations = async () => {
        setIsLoadingHistory(true);
        const response = await getRequest(`/clarence/conversations/${companyId}`, true);
        if (response?.['code'] === 200) {
            const convos = response['body']['conversations'] || [];
            setConversations(convos);
            // If there are conversations, show history view
            if (convos.length > 0) {
                setShowHistory(true);
            } else {
                // No history, start new conversation
                setShowHistory(false);
                await createNewConversation();
            }
        } else {
            setShowHistory(false);
            await createNewConversation();
        }
        setIsLoadingHistory(false);
    };

    const createNewConversation = async () => {
        const response = await postRequest('/clarence/conversation', { companyId }, true);
        if (response?.['code'] === 201 || response?.['code'] === 200) {
            setConversationId(response['body']['conversationId']);
            setSuggestions(response['body']['suggestions'] || []);
            setMessages([]);
            setShowHistory(false);
        }
    };

    const loadConversation = async (convId) => {
        setIsLoading(true);
        const response = await getRequest(`/clarence/conversation/${convId}`, true);
        if (response?.['code'] === 200) {
            const conversation = response['body']['conversation'];
            setConversationId(convId);
            setMessages(conversation['messages'] || []);
            setSuggestions(conversation['context']?.['suggestedPrompts'] || []);
            setShowHistory(false);
        }
        setIsLoading(false);
    };

    const handleNewChat = async () => {
        setIsCreatingConversation(true);
        setMessages([]);
        setConversationId(null);
        setSuggestions([]);
        await createNewConversation();
        setIsCreatingConversation(false);
    };

    const handleBackToHistory = () => {
        setShowHistory(true);
        loadConversations();
    };

    const handleAgentAction = useCallback((action) => {
        setAgentActions(prev => {
            // For tool actions, match by toolName; for others, match by actionType
            const matchKey = action['toolName'] ? 'toolName' : 'actionType';
            const existing = prev.findIndex(a => a[matchKey] === action[matchKey] && a['runId'] === action['runId']);
            if (existing >= 0) {
                const updated = [...prev];
                updated[existing] = action;
                return updated;
            }
            return [...prev, action];
        });
    }, []);

    const handleAgentResponse = useCallback((data) => {
        if (data['message']) {
            setMessages(prev => [...prev, data['message']]);
        }
        if (data['suggestions']) {
            setSuggestions(data['suggestions']);
        }
        setIsLoading(false);
        setAgentActions([]);
    }, []);

    const handleClassification = useCallback((data) => {
        if (data['needsConfirmation']) {
            setPendingClassification(data['classification']);
        }
    }, []);

    const sendMessage = async (content) => {
        if (!content?.trim() || !conversationId || isLoading) return;

        setIsLoading(true);
        setAgentActions([]);

        const userMessage = { role: 'user', content, timestamp: new Date().toISOString() };
        setMessages(prev => [...prev, userMessage]);

        const response = await postRequest('/clarence/chat', { conversationId, message: content, files: selectedFiles }, true, null, true);

        if (response?.['code'] !== 200) {
            setIsLoading(false);
            setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, something went wrong. Please try again.', timestamp: new Date().toISOString() }]);
            return;
        }

        if (response['body']?.['response']) {
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: response['body']['response'],
                timestamp: new Date().toISOString(),
                toolsUsed: response['body']['toolsUsed'] || []
            }]);
            setSuggestions(response['body']['suggestions'] || []);
            setIsLoading(false);
            setAgentActions([]);
        }
    };

    const handleSuggestionClick = (suggestion) => {
        sendMessage(suggestion);
    };

    const handleClassificationConfirm = async (confirmed, correctedType, correctedSectionCode) => {
        if (!pendingClassification) return;
        await postRequest('/clarence/classify/confirm', { conversationId, fileUrl: pendingClassification['fileUrl'], confirmed, correctedType, correctedSectionCode }, true);
        setPendingClassification(null);
    };

    const handleFileSelect = (files) => {
        setSelectedFiles(files);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diff = now - date;
        const minutes = Math.floor(diff / (1000 * 60));
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));

        // For today, show relative time
        if (days === 0) {
            if (minutes < 1) return 'Just now';
            if (minutes < 60) return `${minutes} min ago`;
            return `${hours} hour${hours > 1 ? 's' : ''} ago`;
        }
        if (days === 1) return 'Yesterday';
        if (days < 7) return `${days} days ago`;
        return date.toLocaleDateString();
    };

    // Show loading state
    if (isLoadingHistory) {
        return (
            <div className="clarence-chat">
                <div className="clarence-header">
                    <div className="clarence-header-left">
                        <div className="clarence-avatar">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" fill="currentColor"/>
                            </svg>
                        </div>
                        <div className="clarence-header-info">
                            <h3>Clarence AI</h3>
                            <span className="clarence-status">Ask about your documents</span>
                        </div>
                    </div>
                    <div className="clarence-header-actions">
                        {onExpand && (
                            <button className="clarence-expand-btn" onClick={onExpand} title={isExpanded ? "Collapse" : "Expand"}>
                                {isExpanded ? (
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                ) : (
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                )}
                            </button>
                        )}
                        {onClose && (
                            <button className="clarence-close-btn" onClick={onClose}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                            </button>
                        )}
                    </div>
                </div>
                <div className="clarence-body clarence-loading">
                    <div className="clarence-typing">
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                </div>
            </div>
        );
    }

    // Show conversation history
    if (showHistory && conversations.length > 0) {
        return (
            <div className="clarence-chat">
                <div className="clarence-header">
                    <div className="clarence-header-left">
                        <div className="clarence-avatar">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" fill="currentColor"/>
                            </svg>
                        </div>
                        <div className="clarence-header-info">
                            <h3>Clarence AI</h3>
                            <span className="clarence-status">Your conversations</span>
                        </div>
                    </div>
                    <div className="clarence-header-actions">
                        {onExpand && (
                            <button className="clarence-expand-btn" onClick={onExpand} title={isExpanded ? "Collapse" : "Expand"}>
                                {isExpanded ? (
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                ) : (
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                )}
                            </button>
                        )}
                        {onClose && (
                            <button className="clarence-close-btn" onClick={onClose}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                            </button>
                        )}
                    </div>
                </div>

                <div className="clarence-body">
                    <button className="clarence-new-chat-btn" onClick={handleNewChat} disabled={isCreatingConversation}>
                        {isCreatingConversation ? (
                            <div className="clarence-btn-loader">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                        ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                        )}
                        {isCreatingConversation ? 'Creating...' : 'New Conversation'}
                    </button>

                    <div className="clarence-history-list">
                        {conversations.map((conv) => (
                            <div
                                key={conv['_id']}
                                className="clarence-history-item"
                                onClick={() => loadConversation(conv['_id'])}
                            >
                                <div className="clarence-history-icon">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2v10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </div>
                                <div className="clarence-history-content">
                                    <span className="clarence-history-title">{conv['title'] || 'Conversation'}</span>
                                    <span className="clarence-history-meta">
                                        {conv['messageCount'] || 0} messages • {formatDate(conv['lastMessageAt'] || conv['createdAt'])}
                                    </span>
                                </div>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="clarence-history-arrow">
                                    <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    // Show chat view
    return (
        <div className="clarence-chat">
            <div className="clarence-header">
                <div className="clarence-header-left">
                    {conversations.length > 0 && (
                        <button className="clarence-back-btn" onClick={handleBackToHistory}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                    )}
                    <div className="clarence-avatar">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" fill="currentColor"/>
                        </svg>
                    </div>
                    <div className="clarence-header-info">
                        <h3>Clarence AI</h3>
                        <span className="clarence-status">Ask about your documents</span>
                    </div>
                </div>
                <div className="clarence-header-actions">
                    {onExpand && (
                        <button className="clarence-expand-btn" onClick={onExpand} title={isExpanded ? "Collapse" : "Expand"}>
                            {isExpanded ? (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            ) : (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            )}
                        </button>
                    )}
                    {onClose && (
                        <button className="clarence-close-btn" onClick={onClose}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                        </button>
                    )}
                </div>
            </div>

            <div className="clarence-body">
                {messages.length === 0 && !isLoading && (
                    <div className="clarence-welcome">
                        <div className="clarence-welcome-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" fill="#6366f1" opacity="0.2"/>
                                <path d="M12 6v6l4 2" stroke="#6366f1" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                        </div>
                        <h2>Hello! I'm Clarence</h2>
                        <p>Your AI assistant for document analysis. Ask me anything about your company documents.</p>
                    </div>
                )}

                <ChatMessages messages={messages} />

                {isLoading && agentActions.length > 0 && (
                    <AgentActions actions={agentActions} />
                )}

                {isLoading && agentActions.length === 0 && (
                    <div className="clarence-typing">
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            {messages.length === 0 && suggestions.length > 0 && (
                <SuggestedPrompts suggestions={suggestions} onSelect={handleSuggestionClick} />
            )}

            <ChatInput ref={chatInputRef} onSend={sendMessage} disabled={isLoading} onFileSelect={handleFileSelect} selectedFiles={selectedFiles} />

            {pendingClassification && (
                <ClassificationConfirm classification={pendingClassification} onConfirm={handleClassificationConfirm} onDismiss={() => setPendingClassification(null)} />
            )}
        </div>
    );
};

export default ClarenceChat;
